import React, { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { logLoginActivity } from "./admin/UserActivityReport";
import { message } from "antd";
import apiClient from "../utils/axios";
import { useOrganization } from "../contexts/organization-context";
import { useSubscription } from "../contexts/subscription-context";

export const AuthCallback: React.FC = () => {
  const { isAuthenticated, getAccessTokenSilently, isLoading, error } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasLogged = useRef(false);
  const { refreshOrganizations } = useOrganization();
  const { checkSubscription } = useSubscription();

  const processFacilityInvitation = async (facilityId: string) => {
    console.log("🏥 Processing facility invitation for facility:", facilityId);
    
    try {
      // Ensure we can get a token first
      console.log("🔐 Getting access token...");
      const token = await getAccessTokenSilently();
      console.log("✅ Access token obtained");
      
      console.log("📡 Making API call to accept invitation...");
      const response = await apiClient.post(
        `/api/facility/invitation/accept?facility_id=${facilityId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      console.log("✅ Invitation accepted successfully:", response.data);
      message.success(response.data.message || "Successfully joined facility!");
      
      // Force refresh of all contexts since user now has organization access through facility
      console.log("🔄 Refreshing all contexts...");
      await Promise.all([
        refreshOrganizations(),
        checkSubscription()
      ]);
      console.log("✅ All contexts refreshed");
      
      // Redirect to root page with refresh indicator
      setTimeout(() => {
        navigate("/?refreshAccess=true", { replace: true });
      }, 1500); // Slightly longer delay to ensure contexts are fully updated
      
    } catch (error: any) {
      console.error("❌ Error accepting facility invitation:", error);
      console.error("Error details:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      message.error(
        error.response?.data?.detail || 
        error.message ||
        "Failed to accept facility invitation. Please try again or contact support."
      );
      
      // Redirect to home page with error after delay
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
    }
  };

  useEffect(() => {
    console.log("🔄 Auth callback useEffect triggered", {
      isLoading,
      isAuthenticated,
      hasLogged: hasLogged.current,
      searchParams: Object.fromEntries(searchParams.entries())
    });
    
    if (error) {
      console.error("❌ Auth0 error:", error);
      message.error("Authentication failed. Please try again.");
      navigate("/", { replace: true });
      return;
    }
    
    if (!isLoading && isAuthenticated && !hasLogged.current) {
      hasLogged.current = true;
      console.log("✅ User authenticated, processing callback...");
      
      // Check if this is a facility invitation callback
      const invitationType = searchParams.get("type");
      const facilityId = searchParams.get("facility");
      
      console.log("🔍 Checking URL parameters:", { invitationType, facilityId });
      
      if (invitationType === "facility_invite" && facilityId) {
        console.log("🏥 Facility invitation detected, processing...");
        
        // Process facility invitation with a small delay to ensure Auth0 is fully ready
        logLoginActivity(getAccessTokenSilently)
          .then(() => {
            console.log("📊 Login activity logged, now processing invitation...");
            // Add small delay to ensure everything is ready
            setTimeout(() => {
              processFacilityInvitation(facilityId);
            }, 500);
          })
          .catch((err) => {
            console.error("⚠️ Login activity logging failed:", err);
            // Still try to process the invitation
            setTimeout(() => {
              processFacilityInvitation(facilityId);
            }, 500);
          });
      } else {
        console.log("🏠 Normal login flow, redirecting to home...");
        // Normal login flow
        logLoginActivity(getAccessTokenSilently).finally(() => {
          navigate("/", { replace: true });
        });
      }
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently, navigate, searchParams, error]);

  // Show loading state with more info
  const invitationType = searchParams.get("type");
  const facilityId = searchParams.get("facility");
  
  if (invitationType === "facility_invite" && facilityId) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div>Processing facility invitation...</div>
        <div style={{ color: '#666', fontSize: '14px' }}>
          Please wait while we add you to the facility and set up your access.
        </div>
      </div>
    );
  }

  return <div>Logging you in...</div>;
}; 